import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, fromEvent, merge, of } from "rxjs";
import { map, startWith } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class NetworkStatusService {
  private networkStatusSubject = new BehaviorSubject<boolean>(navigator.onLine);

  constructor() {
    this.initializeNetworkStatusDetection();
  }

  /**
   * Initialize network status detection
   */
  private initializeNetworkStatusDetection(): void {
    // Check if browser supports navigator.onLine
    if (typeof navigator !== "undefined" && "onLine" in navigator) {
      // Create observables for online and offline events
      const online$ = fromEvent(window, "online").pipe(map(() => true));
      const offline$ = fromEvent(window, "offline").pipe(map(() => false));

      // Combine events and start with current status
      merge(online$, offline$)
        .pipe(startWith(navigator.onLine))
        .subscribe((isOnline) => {
          this.networkStatusSubject.next(isOnline);
        });
    } else {
      // If no support, assume always online
      this.networkStatusSubject.next(true);
    }
  }

  /**
   * Returns an observable that emits the connection status
   * @returns Observable<boolean> - true if connected, false if not
   */
  getNetworkStatus(): Observable<boolean> {
    return this.networkStatusSubject.asObservable();
  }

  /**
   * Returns an observable that emits only when connection is lost
   * @returns Observable<boolean> - emits false when connection is lost
   */
  getOfflineStatus(): Observable<boolean> {
    return this.networkStatusSubject
      .asObservable()
      .pipe(map((isOnline) => !isOnline));
  }

  /**
   * Returns an observable that emits only when connection is recovered
   * @returns Observable<boolean> - emits true when connection is recovered
   */
  getOnlineStatus(): Observable<boolean> {
    return this.networkStatusSubject
      .asObservable()
      .pipe(map((isOnline) => isOnline));
  }

  /**
   * Gets the current connection status synchronously
   * @returns boolean - true if connected, false if not
   */
  getCurrentNetworkStatus(): boolean {
    return this.networkStatusSubject.value;
  }

  /**
   * Checks if there is currently an internet connection
   * @returns boolean - true if connected, false if not
   */
  isOnline(): boolean {
    return this.getCurrentNetworkStatus();
  }

  /**
   * Checks if there is currently NO internet connection
   * @returns boolean - true if disconnected, false if connected
   */
  isOffline(): boolean {
    return !this.getCurrentNetworkStatus();
  }

  /**
   * Method for testing - allows simulating network status changes
   * @param isOnline - connection status to simulate
   */
  simulateNetworkChange(isOnline: boolean): void {
    this.networkStatusSubject.next(isOnline);
  }
}
